export type PromptPackSafetyInput = {
  accountName?: string | null;
  businessName?: string | null;
  displayName?: string | null;
  packKey?: string | null;
  packLabel?: string | null;
  sourceAccountName?: string | null;
  categoryKey?: string | null;
  productType?: string | null;
};

export type PromptPackSafetyResult = {
  status: "safe" | "warning" | "blocked";
  title: string;
  message: string;
  reasons: string[];
};

const AUTO_DEALER_SOURCE_CONTAMINATION_TERMS = [
  "quality chevrolet gmc",
  "chevrolet gmc",
  "gmc",
  "chevrolet",
  "buick",
  "cadillac",
];

function clean(value?: string | null): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function hasMeaningfulOverlap(a?: string | null, b?: string | null): boolean {
  const left = clean(a);
  const right = clean(b);

  if (!left || !right) return false;
  if (left === right) return true;
  if (left.includes(right) || right.includes(left)) return true;

  const leftTokens = new Set(left.split(" ").filter((token) => token.length > 2));
  const rightTokens = right.split(" ").filter((token) => token.length > 2);

  return rightTokens.some((token) => leftTokens.has(token));
}

export function getPromptPackSafety(input: PromptPackSafetyInput): PromptPackSafetyResult {
  const accountName = input.businessName || input.displayName || input.accountName || "";
  const account = clean(accountName);
  const source = clean(input.sourceAccountName);
  const packKey = clean(input.packKey);
  const packLabel = clean(input.packLabel);
  const categoryKey = clean(input.categoryKey);
  const productType = clean(input.productType);

  const reasons: string[] = [];

  const sourceLooksDifferent =
    Boolean(source) &&
    Boolean(account) &&
    !hasMeaningfulOverlap(source, account);

  const isAutoDealer =
    categoryKey.includes("auto") ||
    categoryKey.includes("dealer") ||
    packKey.includes("auto dealer") ||
    packLabel.includes("auto dealer");

  const accountLooksAcura =
    account.includes("acura") ||
    packKey.includes("acura") ||
    packLabel.includes("acura");

  const sourceLooksGm =
    AUTO_DEALER_SOURCE_CONTAMINATION_TERMS.some((term) =>
      ` ${source} ${packKey} ${packLabel} `.includes(` ${term} `)
    ) ||
    source.includes("quality chevrolet gmc") ||
    source.includes("chevrolet gmc");

  const genericStarterWithSource =
    packKey === "local auto dealer 72" ||
    packKey === "local auto dealer starter" ||
    packKey === "local auto dealer";

  if (sourceLooksDifferent) {
    reasons.push(`Source account does not match selected account: ${input.sourceAccountName}`);
  }

  if (isAutoDealer && accountLooksAcura && sourceLooksGm) {
    reasons.push("Auto dealer pack appears to contain GM/Chevrolet/GMC source context for an Acura account.");
  }

  if (genericStarterWithSource && sourceLooksDifferent) {
    reasons.push("This starter pack has a source dealership and should be treated as a template, not a safe reusable pack.");
  }

  if (productType && productType !== "local") {
    reasons.push(`Product type is ${input.productType}, not local.`);
  }

  if (reasons.length >= 2 || (accountLooksAcura && sourceLooksGm)) {
    return {
      status: "blocked",
      title: "Blocked by Agent Smith safety check",
      message:
        "This prompt pack appears source-contaminated for the selected account. Generate an Agent Smith custom pack instead.",
      reasons,
    };
  }

  if (reasons.length === 1) {
    return {
      status: "warning",
      title: "Review before applying",
      message:
        "This prompt pack may not be account-safe. Prefer an Agent Smith custom pack unless this is intentional.",
      reasons,
    };
  }

  return {
    status: "safe",
    title: "Prompt pack looks account-safe",
    message: "No obvious source-account mismatch detected.",
    reasons: [],
  };
}

export function isPromptPackBlocked(input: PromptPackSafetyInput): boolean {
  return getPromptPackSafety(input).status === "blocked";
}
